import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  CopyButton,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";

import { api, type AuditResponse } from "../lib/api";

const LIMIT_OPTIONS = [
  { value: "50",  label: "50 רשומות" },
  { value: "100", label: "100 רשומות" },
  { value: "200", label: "200 רשומות" },
  { value: "500", label: "500 רשומות" },
];

const ACTION_LABELS: Record<string, string> = {
  "campaign.created":    "קמפיין נוצר",
  "campaign.deleted":    "קמפיין נמחק",
  "run.created":         "ריצה נוצרה",
  "run.deleted":         "ריצה נמחקה",
  "invite.created":      "הזמנה נשלחה",
  "invite.bulk_created": "הזמנות נשלחו",
  "invite.removed":      "הוזמן הוסר",
  "file.uploaded":       "קובץ הועלה",
  "file.approved":       "קובץ אושר",
  "file.rejected":       "קובץ נדחה",
  "auth.login":          "כניסה למערכת",
  "auth.logout":         "יציאה מהמערכת",
  "settings.updated":    "הגדרות עודכנו",
};

function actionColor(action: string): string {
  if (action.startsWith("campaign")) return "blue";
  if (action.startsWith("run"))      return "cyan";
  if (action.startsWith("invite"))   return "orange";
  if (action.startsWith("file"))     return "teal";
  if (action.startsWith("auth"))     return "gray";
  if (action.startsWith("settings")) return "violet";
  return "gray";
}

function formatTs(ts: string): { primary: string; secondary: string } {
  const d = new Date(ts);
  const primary = d.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
  const secondary = d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  return { primary, secondary };
}

export function AuditScreen() {
  const [data, setData] = useState<AuditResponse | null>(null);
  const [limit, setLimit] = useState("200");
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    api
      .audit(parseInt(limit, 10))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError("שגיאה בטעינת יומן הביקורת."); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [limit]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter(
      (r) =>
        r.action.toLowerCase().includes(q) ||
        r.actor.toLowerCase().includes(q) ||
        (r.resource_id ?? "").toLowerCase().includes(q) ||
        (ACTION_LABELS[r.action] ?? "").includes(q),
    );
  }, [data, filter]);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>יומן ביקורת</Title>
        <Select
          data={LIMIT_OPTIONS}
          value={limit}
          onChange={(v) => setLimit(v ?? "200")}
          w={140}
          size="sm"
        />
      </Group>

      {/* Chain integrity banner */}
      {data && (
        data.chain_verified ? (
          <Paper withBorder p="sm" style={{ borderColor: "var(--mantine-color-teal-4)", background: "var(--mantine-color-teal-0)" }}>
            <Group gap="xs">
              <Text size="lg">✓</Text>
              <Stack gap={0}>
                <Text size="sm" fw={600} c="teal.8">שרשרת הביקורת תקינה</Text>
                <Text size="xs" c="teal.7">כל הרשומות אומתו — לא זוהה שינוי לא מורשה.</Text>
              </Stack>
            </Group>
          </Paper>
        ) : (
          <Alert color="red" title="שרשרת הביקורת פגומה" icon="⚠">
            זוהתה אי-התאמה בחתימות. ייתכן שרשומות שונו לא כדין. יש לפנות לתמיכה.
          </Alert>
        )
      )}

      {/* Filter */}
      {data && data.rows.length > 0 && (
        <TextInput
          placeholder="סינון לפי פעולה, משתמש, משאב…"
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          rightSection={
            filter ? (
              <ActionIcon variant="subtle" size="sm" onClick={() => setFilter("")}>✕</ActionIcon>
            ) : null
          }
        />
      )}

      {error && <Text c="red">{error}</Text>}

      {busy && !data && (
        <Group py="xl" justify="center">
          <Loader size="sm" />
        </Group>
      )}

      {data && (
        <Card withBorder padding={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 48, color: "var(--mantine-color-dimmed)", fontWeight: 400 }}>#</Table.Th>
                <Table.Th>תאריך ושעה</Table.Th>
                <Table.Th>פעולה</Table.Th>
                <Table.Th>משתמש</Table.Th>
                <Table.Th>משאב</Table.Th>
                <Table.Th style={{ width: 120 }}>חתימה</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => {
                const { primary, secondary } = formatTs(row.ts);
                const label = ACTION_LABELS[row.action] ?? row.action;
                const color = actionColor(row.action);
                return (
                  <Table.Tr key={row.seq}>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{row.seq}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm">{primary}</Text>
                        <Text size="xs" c="dimmed">{secondary}</Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={color} variant="light" radius="sm">
                        {label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" dir="ltr" style={{ fontFamily: "monospace" }}>
                        {row.actor}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {row.resource_id ? (
                        <Badge variant="outline" color="gray" radius="sm" dir="ltr">
                          {row.resource_id}
                        </Badge>
                      ) : (
                        <Text c="dimmed" size="sm">—</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Text size="xs" dir="ltr" style={{ fontFamily: "monospace", color: "var(--mantine-color-dimmed)" }}>
                          {row.this_hash.slice(0, 10)}…
                        </Text>
                        <CopyButton value={row.this_hash}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "הועתק" : "העתק חתימה"} withArrow>
                              <ActionIcon
                                size="xs"
                                variant="subtle"
                                color={copied ? "teal" : "gray"}
                                onClick={copy}
                              >
                                {copied ? "✓" : "⎘"}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {filter ? "לא נמצאו רשומות התואמות את הסינון." : "אין רשומות ביומן."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data.rows.length > 0 && (
            <Group px="md" py="xs" justify="flex-end">
              <Text size="xs" c="dimmed">
                {filter
                  ? `${rows.length} מתוך ${data.rows.length} רשומות`
                  : `${data.rows.length} רשומות`}
              </Text>
            </Group>
          )}
        </Card>
      )}
    </Stack>
  );
}
